"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
var dom_1 = require("../utils/dom");
var misc_1 = require("../utils/misc");
var misc_2 = require("./misc");
/*
 * Create an Obvservable that enables resizing an HTML element
 * and emits a stream of updated size.
 *
 * @param element HTML Element for which to enable resizing
 * @param handle HTML Element of the movable handle
 */
exports.createResizeObservable = function (_a) {
    var element = _a.element, handle = _a.handle, onComplete = _a.onComplete, _b = _a.shouldConvertToPercent, shouldConvertToPercent = _b === void 0 ? true : _b, snapTo = _a.snapTo, snapXTo = _a.snapXTo, snapYTo = _a.snapYTo, top = _a.top, right = _a.right, bottom = _a.bottom, left = _a.left;
    var mouseDown$ = rxjs_1.fromEvent(handle, 'mousedown');
    return mouseDown$.pipe(operators_1.filter(function (e) { return e.which === 1; }), // left clicks only
    operators_1.switchMap(function (e) {
        e.preventDefault();
        e.stopPropagation();
        var oldOffsetAndSize = dom_1.offsetAndSizeOfElement(element);
        var oldRotation = dom_1.rotationOfElement(element);
        var transformationMatrix = dom_1.transformationMatrixOfElement(element);
        var scale = dom_1.scaleOfElement(element);
        var snapValues = misc_1.getSnapValues(snapTo, snapXTo, snapYTo);
        var move$ = misc_2.documentMouseMove$.pipe(operators_1.map(angleAndDistanceFromPointToMouseEvent(e.clientX, e.clientY, scale)), operators_1.map(horizontalAndVerticalChange(oldRotation)), operators_1.map(applyToOriginalSize(oldOffsetAndSize, top, right, bottom, left)), operators_1.map(limitToTwentyPxMinimum), operators_1.map(misc_1.snapPositionValues(snapValues)), operators_1.distinctUntilChanged(), operators_1.withLatestFrom(misc_2.shiftIsPressed$), operators_1.map(lockAspectRatio(oldOffsetAndSize.width / oldOffsetAndSize.height)), operators_1.map(offsetForVisualConsistency(oldOffsetAndSize, transformationMatrix, top, right, bottom, left)), operators_1.map(convertSizeToPercent(shouldConvertToPercent, element.parentElement)));
        return misc_2.requestAnimationFramesUntil(move$, misc_2.documentMouseUp$, onComplete);
    }));
};
/**
 * Calculates the distance from an origin point to a mouse event
 */
var angleAndDistanceFromPointToMouseEvent = function (originX, originY, scale) { return function (e) { return ({
    angle: misc_1.angleBetweenPoints(originX, originY)(e.clientX, e.clientY),
    distance: misc_1.distanceBetweenPoints(originX, originY)(e.clientX, e.clientY) / scale,
}); }; };
/**
 * Translate angle and distance change of handle to change in x and y
 * (taking old rotation of the element into account).
 */
var horizontalAndVerticalChange = function (oldRotation) { return function (angleAndDistanceChange) {
    var angleRadians = ((angleAndDistanceChange.angle - oldRotation) * Math.PI) / 180;
    return {
        width: angleAndDistanceChange.distance * Math.cos(angleRadians),
        height: angleAndDistanceChange.distance * Math.sin(angleRadians),
    };
}; };
/**
 * Apply horizontal and vertical change to old element's Size.
 */
var applyToOriginalSize = function (oldOffsetAndSize, top, right, bottom, left) { return function (change) {
    var positionLeft = left
        ? oldOffsetAndSize.left + change.width
        : oldOffsetAndSize.left;
    var positionTop = top
        ? oldOffsetAndSize.top + change.height
        : oldOffsetAndSize.top;
    var positionWidth = left
        ? oldOffsetAndSize.width - change.width
        : right
            ? oldOffsetAndSize.width + change.width
            : oldOffsetAndSize.width;
    var positionHeight = top
        ? oldOffsetAndSize.height - change.height
        : bottom
            ? oldOffsetAndSize.height + change.height
            : oldOffsetAndSize.height;
    return {
        left: positionLeft,
        top: positionTop,
        width: positionWidth,
        height: positionHeight,
    };
}; };
/**
 * Limit the Size to a twenty pixel minimum height and width.
 */
var limitToTwentyPxMinimum = function (position) { return (__assign(__assign({}, position), { height: Math.max(20, position.height), width: Math.max(20, position.width) })); };
/**
 * If `shouldLock` is `true`, the new Size
 * will be forced into the provided aspect ratio.
 */
var lockAspectRatio = function (aspectRatio) { return function (_a) {
    var position = _a[0], shouldLock = _a[1];
    if (!shouldLock) {
        return position;
    }
    if (position.width / position.height > aspectRatio) {
        return __assign(__assign({}, position), { height: position.width / aspectRatio });
    }
    if (position.width / position.height < aspectRatio) {
        return __assign(__assign({}, position), { width: position.height * aspectRatio });
    }
    return position;
}; };
/**
 * Fudge the left and top in order to keep
 * the perceived visual position the same.
 */
var offsetForVisualConsistency = function (oldOffsetAndSize, transformationMatrix, top, right, bottom, left) { return function (newOffsetAndSize) {
    var oldCorners = dom_1.visualCorners(oldOffsetAndSize, transformationMatrix);
    var newCorners = dom_1.visualCorners(newOffsetAndSize, transformationMatrix);
    var changeX;
    var changeY;
    if (bottom && left) {
        changeX = newCorners.ne.x - oldCorners.ne.x;
        changeY = newCorners.ne.y - oldCorners.ne.y;
    }
    else if (top && right) {
        changeX = newCorners.sw.x - oldCorners.sw.x;
        changeY = newCorners.sw.y - oldCorners.sw.y;
    }
    else if (top || left) {
        changeX = newCorners.se.x - oldCorners.se.x;
        changeY = newCorners.se.y - oldCorners.se.y;
    }
    else {
        changeX = newCorners.nw.x - oldCorners.nw.x;
        changeY = newCorners.nw.y - oldCorners.nw.y;
    }
    return {
        left: newOffsetAndSize.left - changeX,
        top: newOffsetAndSize.top - changeY,
        width: newOffsetAndSize.width,
        height: newOffsetAndSize.height,
    };
}; };
/**
 * Convert pixel Size to a percentage of the parent's Size.
 */
var convertSizeToPercent = function (shouldConvertToPercent, parent) { return function (position) {
    return shouldConvertToPercent
        ? {
            left: misc_1.round((position.left / parent.offsetWidth) * 100) + "%",
            top: misc_1.round((position.top / parent.offsetHeight) * 100) + "%",
            height: misc_1.round((position.height / parent.offsetHeight) * 100) + "%",
            width: misc_1.round((position.width / parent.offsetWidth) * 100) + "%",
        }
        : {
            left: misc_1.round(position.left) + "px",
            top: misc_1.round(position.top) + "px",
            height: misc_1.round(position.height) + "px",
            width: misc_1.round(position.width) + "px",
        };
}; };
//# sourceMappingURL=resize.js.map